<template>
    <div class="relative flex h-full w-full items-center justify-center">
        <div class="relative">
            <div class="relative">
                <div
                    class="absolute -left-[1.4rem] top-1/2 z-20 flex h-1/2 -translate-y-1/2 flex-col justify-between">
                    <CircleTooltip
                        v-for="(item, index) in tab.circles.slice(0, 2)"
                        :key="index"
                        v-show="item.text"
                        :id="index"
                        v-model="activeCircle"
                        :text="item.text"
                        @change-direction-rotate="changeDirectionRotate" />
                </div>
                <div
                    class="absolute -right-[1.35rem] top-1/2 z-20 flex h-[107%] -translate-y-1/2 flex-col justify-between">
                    <CircleTooltip
                        v-for="(item, index) in tab.circles.slice(2)"
                        :key="index + 2"
                        v-show="item.text"
                        :id="index + 2"
                        v-model="activeCircle"
                        text-direction="right"
                        :text="item.text"
                        @change-direction-rotate="changeDirectionRotate" />
                </div>
                <CromiIcon class="h-auto w-[25rem] xl:w-[31.25rem]" />
            </div>
        </div>
        <template v-if="isThreeDModel">
            <NuxtLink
                v-if="tab.to"
                :to="tab.to"
                :aria-label="modelLinkLabel"
                :class="[
                    threeDModelFallbackClass,
                    {
                        'pointer-events-none': isThreeDReady,
                    },
                ]"
                class="absolute z-10">
                <picture
                    :class="{ 'opacity-0': isThreeDReady }"
                    class="transition-opacity duration-300">
                    <source
                        type="image/webp"
                        :srcset="tab.image_webp" />
                    <img
                        width="549"
                        height="934"
                        loading="eager"
                        fetchpriority="high"
                        decoding="async"
                        :alt="tab.title"
                        :src="tab.image"
                        class="w-full" />
                </picture>
            </NuxtLink>
            <picture
                v-else
                :class="[
                    threeDModelFallbackClass,
                    { 'opacity-0': isThreeDReady },
                ]"
                class="pointer-events-none absolute z-10 transition-opacity duration-300">
                <source
                    type="image/webp"
                    :srcset="tab.image_webp" />
                <img
                    width="549"
                    height="934"
                    loading="eager"
                    fetchpriority="high"
                    decoding="async"
                    :alt="tab.title"
                    :src="tab.image"
                    class="w-full" />
            </picture>
            <div
                :key="tab.slug"
                class="absolute transition-all duration-300">
                <ClientOnly>
                    <LazyThreeDSection
                        v-if="shouldRenderThreeD"
                        :model="threeDModelName"
                        :tab="tab"
                        @ready="isThreeDReady = true" />
                </ClientOnly>
            </div>
        </template>

        <NuxtLink
            v-else-if="tab.to"
            :to="tab.to"
            :aria-label="modelLinkLabel"
            :key="tab.slug"
            :class="{
                'max-xl:w-[22rem] xl:w-[24rem]': tab.slug === 'audioguides-tab',
                'max-xl:w-[28rem] xl:w-[36rem]':
                    tab.slug === 'sync-translate-tab',
                'max-xl:w-[32rem]': tab.slug === 'accessible-environment-tab',
                'rotate-12': tab.circles.length && directionRotate === 'left',
                '-rotate-12': tab.circles.length && directionRotate === 'right',
            }"
            class="absolute cursor-pointer transition-all duration-300"
            @mousemove="onMouseMove"
            @mouseenter="onMouseEnter"
            @mouseleave="onMouseLeave">
            <picture>
                <source
                    type="image/webp"
                    :srcset="tab.image_webp" />

                <img
                    width="549"
                    height="934"
                    loading="eager"
                    fetchpriority="high"
                    decoding="async"
                    :alt="tab.title"
                    :src="tab.image"
                    class="w-full" />
            </picture>
        </NuxtLink>
        <picture
            v-else
            :key="tab.slug"
            :class="{
                'max-xl:w-[22rem] xl:w-[24rem]': tab.slug === 'audioguides-tab',
                'max-xl:w-[28rem] xl:w-[36rem]':
                    tab.slug === 'sync-translate-tab',
                'max-xl:w-[32rem]': tab.slug === 'accessible-environment-tab',
                'rotate-12': tab.circles.length && directionRotate === 'left',
                '-rotate-12': tab.circles.length && directionRotate === 'right',
            }"
            class="absolute transition-all duration-300"
            @mousemove="onMouseMove"
            @mouseenter="onMouseEnter"
            @mouseleave="onMouseLeave">
            <source
                type="image/webp"
                :srcset="tab.image_webp" />
            <img
                width="549"
                height="934"
                loading="eager"
                fetchpriority="high"
                decoding="async"
                :alt="tab.title"
                :src="tab.image"
                class="w-full" />
        </picture>
        <ClientOnly>
            <Teleport to="#teleports">
                <ModelTooltip
                    :is-hover-model="isHoverModel && hasMouseMoved"
                    :tab
                    :top="tooltipPos.y"
                    :left="tooltipPos.x" />
            </Teleport>
        </ClientOnly>
    </div>
</template>

<script setup lang="ts">
import CromiIcon from '~/components/pages/home/main-section/svg/CromiIcon.vue'
import CircleTooltip from '~/components/particles/first-screen/CircleTooltip.vue'
import type { TabInfo } from '~/components/particles/first-screen/tab.types'
import ModelTooltip from '~/components/particles/model-tooltip/ModelTooltip.vue'

const THREE_D_DEFER_DELAY_MS = 1200

const LazyThreeDSection = defineAsyncComponent(
    () => import('~/components/particles/threed-section/ThreeDSection.vue')
)

const props = defineProps({
    tabs: {
        type: Array as PropType<TabInfo[]>,
        default: () => [],
    },
    tab: {
        type: Object as PropType<TabInfo>,
        default: () => {},
    },
})
const activeCircle: Ref<number | null> = ref(null)
const directionRotate: Ref<'left' | 'right'> = ref('left')
type ThreeDModelName = 'radioguides-tab' | 'cabs-tab'
function isThreeDModelSlug(slug: string | undefined): slug is ThreeDModelName {
    return slug === 'radioguides-tab' || slug === 'cabs-tab'
}
const isThreeDModel = computed(() => {
    return isThreeDModelSlug(props.tab.slug)
})
const threeDModelName = computed<ThreeDModelName>(() => {
    return isThreeDModelSlug(props.tab.slug)
        ? props.tab.slug
        : 'radioguides-tab'
})
const isThreeDReady = ref(false)
const canLoadThreeD = ref(false)
const threeDModelFallbackClass = computed(() => {
    return props.tab.slug === 'radioguides-tab'
        ? 'w-[15rem] xl:w-[18rem]'
        : 'w-[22rem] xl:w-[24rem]'
})
const shouldRenderThreeD = computed(() => {
    return isThreeDModel.value && canLoadThreeD.value
})
const modelLinkLabel = computed(() => {
    return (props.tab.tooltip?.trim() || stripHtml(props.tab.title))
        .replaceAll('&nbsp;', ' ')
        .replace(/\s+/g, ' ')
        .trim()
})

const isHoverModel = ref(false)
const hasMouseMoved = ref(false)
const tooltipPos = reactive({
    x: 0,
    y: 0,
})
let desktopMediaQuery: MediaQueryList | null = null
let threeDLoadTimer: number | null = null

function scheduleThreeDLoad(): void {
    if (canLoadThreeD.value || threeDLoadTimer !== null) return

    threeDLoadTimer = window.setTimeout(() => {
        canLoadThreeD.value = true
        threeDLoadTimer = null
    }, THREE_D_DEFER_DELAY_MS)
}

function resetThreeDLoadTimer(): void {
    if (threeDLoadTimer === null) return

    window.clearTimeout(threeDLoadTimer)
    threeDLoadTimer = null
}

function handleDesktopMediaChange(event: MediaQueryListEvent): void {
    if (event.matches) {
        scheduleThreeDLoad()
    }
}

function onMouseEnter(): void {
    isHoverModel.value = true
}

function onMouseLeave(): void {
    isHoverModel.value = false
}

function onMouseMove(e: MouseEvent): void {
    hasMouseMoved.value = true

    setPosition(e)
}

function setPosition(e: MouseEvent): void {
    tooltipPos.x = e.clientX
    tooltipPos.y = e.clientY
}

function changeDirectionRotate(id: number): void {
    if (activeCircle.value === id) return

    directionRotate.value = directionRotate.value === 'left' ? 'right' : 'left'
}

onMounted(() => {
    activeCircle.value = 1
    desktopMediaQuery = window.matchMedia('(min-width: 1024px)')

    if (desktopMediaQuery.matches) {
        scheduleThreeDLoad()
    }

    desktopMediaQuery.addEventListener('change', handleDesktopMediaChange)
})

onBeforeUnmount(() => {
    resetThreeDLoadTimer()
    desktopMediaQuery?.removeEventListener('change', handleDesktopMediaChange)
    desktopMediaQuery = null
})

watch(
    () => props.tab.slug,
    () => {
        isThreeDReady.value = false
    }
)
</script>
